import { useEffect, useRef } from "react";
import { playNewMessageSound } from "../lib/notificationSound";

/**
 * useNewMessageNotification — plays the synthesized notification sound when a
 * new incoming message appears in `messages`. The hook is purposely silent on
 * mount (so existing history doesn't blast a pop) and only fires when:
 *   1. We've initialized with a baseline last-message id, AND
 *   2. The newest message's id differs from the cached one, AND
 *   3. `isFromOther(newestMessage)` returns truthy.
 *
 * `messages` should be the current list of messages for the active thread.
 * `resetKey` (optional): when this value changes, the baseline is reset so
 * switching threads doesn't trigger a false notification.
 */
export default function useNewMessageNotification(messages, isFromOther, resetKey) {
  const lastIdRef = useRef(null);
  const readyRef = useRef(false);
  const isFromOtherRef = useRef(isFromOther);
  const keyRef = useRef(resetKey);

  useEffect(() => {
    isFromOtherRef.current = isFromOther;
  }, [isFromOther]);

  useEffect(() => {
    if (keyRef.current !== resetKey) {
      // Thread / context switched — re-baseline silently.
      keyRef.current = resetKey;
      lastIdRef.current = null;
      readyRef.current = false;
    }
    if (!Array.isArray(messages) || messages.length === 0) {
      return;
    }
    const newest = messages[messages.length - 1];
    if (!newest || !newest.id) return;

    if (!readyRef.current) {
      lastIdRef.current = newest.id;
      readyRef.current = true;
      return;
    }

    if (newest.id !== lastIdRef.current) {
      lastIdRef.current = newest.id;
      try {
        if (isFromOtherRef.current?.(newest)) {
          playNewMessageSound();
        }
      } catch {
        /* ignore */
      }
    }
  }, [messages, resetKey]);
}
